import { motion, useTransform } from 'framer-motion';
import styled from 'styled-components';

import { useHeaderUI } from './HeaderUIProvider';

export type Position = 'tl' | 'tr' | 'bl' | 'br';

const VerticalBar = styled.div`
  position: relative;
  overflow: clip;
  height: 1rem;
  width: 1px;
  --dir: 0deg;

  &::after {
    content: '';
    background-color: transparent;
    background-image: ${({ theme }) => {
      const stop1 = theme.paletteCssVars.grid1200;
      const stop2 = theme.paletteCssVars.fgA100;
      return `linear-gradient(var(--dir), ${stop1} 50%, ${stop2})`;
    }};
    position: absolute;
    inset: 0;
  }
`;

const HorizontalBar = styled.div`
  position: relative;
  overflow: clip;
  width: 1rem;
  height: 1px;
  --dir: 90deg;

  &::after {
    content: '';
    background-color: transparent;
    background-image: ${({ theme }) => {
      const stop1 = theme.paletteCssVars.grid1200;
      const stop2 = theme.paletteCssVars.fgA100;
      return `linear-gradient(var(--dir), ${stop1} 50%, ${stop2})`;
    }};
    position: absolute;
    inset: 0;
  }
`;

const LContainer = styled.div<{ 'data-position': Position }>`
  --progress: 0;

  position: absolute;
  display: grid;
  place-items: center;
  z-index: 2;

  & > * {
    grid-area: 1 / -1;
  }

  &[data-position='tl'] {
    inset: 0.5px auto auto -0.5px;
    transform: translate(-50%, -50%);

    ${VerticalBar}::after {
      --dir: 180deg;
      transform-origin: 50% 0%;
      transform: translateY(calc(var(--progress) * 100%));
    }
    ${HorizontalBar}::after {
      transform-origin: 0% 50%;
      transform: translateX(calc(var(--progress) * 100%));
    }
  }
  &[data-position='tr'] {
    inset: 0.5px -0.5px auto auto;
    transform: translate(50%, -50%);

    ${VerticalBar}::after {
      --dir: 180deg;
      transform-origin: 50% 0%;
      transform: translateY(calc(var(--progress) * 100%));
    }
    ${HorizontalBar}::after {
      --dir: 270deg;
      transform-origin: 100% 50%;
      transform: translateX(calc(var(--progress) * -100%));
    }
  }
  &[data-position='bl'] {
    inset: auto auto 0.5px -0.5px;
    transform: translate(-50%, 50%);

    ${VerticalBar}::after {
      transform-origin: 50% 100%;
      transform: translateY(calc(var(--progress) * -100%));
    }
    ${HorizontalBar}::after {
      transform-origin: 0% 50%;
      transform: translateX(calc(var(--progress) * 100%));
    }
  }
  &[data-position='br'] {
    inset: auto -0.5px 0.5px auto;
    transform: translate(50%, 50%);

    ${VerticalBar}::after {
      transform-origin: 50% 100%;
      transform: translateY(calc(var(--progress) * -100%));
    }
    ${HorizontalBar}::after {
      --dir: 270deg;
      transform-origin: 100% 50%;
      transform: translateX(calc(var(--progress) * -100%));
    }
  }
`;

export function NavLAccent({ position }: { position: Position }) {
  const { smoothSquishProgress } = useHeaderUI();
  const progress = useTransform(smoothSquishProgress, [0, 1], [-1, 0.5]);

  return (
    <LContainer data-position={position}>
      <VerticalBar
        as={motion.div}
        style={{ '--progress': progress, opacity: smoothSquishProgress }}
      />
      <HorizontalBar
        as={motion.div}
        style={{ '--progress': progress, opacity: smoothSquishProgress }}
      />
    </LContainer>
  );
}
